import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { QRCodeCard } from '@/components/QRCodeCard'
import { AppButton } from '@/components/AppButton'
import { StickyHeaderNavigation } from '@/components'
import { SmallInfo } from '@/assets/icons'
import {
  CurrencyDepositParams,
  useGetOrCreateCryptoAddress,
} from '@/domain/wallet'
import { DepositInstructionsModal } from '@/components/DepositInstructionsModal'
import { useGetAboutCurrencyInBlockchain } from '@/domain/currencies'
import {
  BlockchainCatalogsDTO,
  useGetBlockchainsCatalogs,
} from '@/domain/blockchains'
import {
  SelectBlockchainModal,
  NetworkSelector,
} from '@/features/currency-deposit/components'
import { useDeviceShare } from '@/hooks/useDeviceShare'
import { useTheme } from '@/hooks/useTheme'
import { formatAddress } from '@/utils/formatAddress'
import { currencyUtils } from '@/utils'
import useOffsets from '@/hooks/useOffsets'

const { formatDynamicCurrency } = currencyUtils

export const CurrencyDepositScreen: React.FC = () => {
  const offsets = useOffsets()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { share, copy } = useDeviceShare()
  const theme = useTheme()

  // Get currency data from state or use mock data
  const passedCurrency = location.state?.currency as CurrencyDepositParams

  // Mock data for quick testing
  const mockCurrency: CurrencyDepositParams = {
    code: 'USDT',
    name: 'Tether USD',
    displayCurrencyCode: 'USDT',
    amount: 1234.56,
    amountWithoutNumberConversion: 1234.56,
    icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
    avatarBGRefColor: '#3CC67',
    networkImageUrl: '',
    networkName: 'Tron',
    networkArrivalTime: 5,
    networkCode: 'TRX',
    selectedCurrencyImageUrl:
      'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
    selectedCurrencyReferenceColor: '#3CC67',
    minimumDepositAmount: 1,
  }

  const currency = passedCurrency || mockCurrency
  const passedBlockchains = location.state
    ?.blockchains as BlockchainCatalogsDTO[]
  const passedSelectedBlockchain = location.state
    ?.selectedBlockchain as BlockchainCatalogsDTO

  // State for managing blockchains
  const [selectedBlockchain, setSelectedBlockchain] =
    useState<BlockchainCatalogsDTO | null>(passedSelectedBlockchain || null)
  const [isBlockchainModalOpen, setIsBlockchainModalOpen] = useState(false)

  // State for instructions modal
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false)

  // API request to get available blockchains
  const { data: blockchainsData, isLoading: isLoadingBlockchains } =
    useGetBlockchainsCatalogs({
      currency_isin: currency?.code || '',
    })

  const blockchains = useMemo(() => {
    return passedBlockchains || (blockchainsData as any)?.blockchains || []
  }, [passedBlockchains, blockchainsData])

  // Auto-select blockchain on load
  useEffect(() => {
    if (blockchains.length > 0 && !selectedBlockchain) {
      // If single blockchain - select automatically
      // If multiple - select the first by default
      setSelectedBlockchain(blockchains[0])
    }
  }, [blockchains, selectedBlockchain])

  const handleBlockchainSelect = (blockchain: BlockchainCatalogsDTO) => {
    setSelectedBlockchain(blockchain)
    setIsBlockchainModalOpen(false)
    // QR code will auto-update due to selectedBlockchain dependency in useGetOrCreateCryptoAddress
  }

  const handleOpenBlockchainModal = () => {
    setIsBlockchainModalOpen(true)
  }

  // API request to get wallet address
  const {
    data: addressData,
    isLoading: isLoadingAddress,
    error: addressError,
  } = useGetOrCreateCryptoAddress({
    currency: currency?.code || '',
    network_code: selectedBlockchain?.code || '',
  })

  // API request to get currency info for blockchain
  const { data: currencyInBlockchainData, isLoading: isLoadingCurrencyInfo } =
    useGetAboutCurrencyInBlockchain({
      currency_isin: currency?.code || '',
      blockchain_code: selectedBlockchain?.code || '',
    })

  // Calculate minimum deposit amount
  const minimumDepositAmount =
    currencyInBlockchainData?.min_deposit_amount &&
    currencyInBlockchainData?.factor
      ? currencyInBlockchainData.min_deposit_amount /
        currencyInBlockchainData.factor
      : 0

  const walletAddress = addressData?.address || ''

  const handleCopyAddress = () => {
    if (walletAddress) {
      copy(walletAddress)
    }
  }

  const handleShareAddress = () => {
    if (walletAddress) {
      share({
        message: walletAddress,
        title: `${currency?.code} Wallet Address`,
      })
    }
  }

  if (!currency) {
    return (
      <BaseView
        style={{
          paddingTop: `${offsets.top + 70}px`,
          height: '100vh',
          paddingLeft: '13px',
          paddingRight: '13px',
        }}
      >
        <StickyHeaderNavigation title={t('translation:deposit')} />
        <BaseView flex={1} bgColor="background.screen" center>
          <BaseText color="label.default">Currency data not found</BaseText>
          <AppButton
            label="Go Back"
            onClick={() => navigate(-1)}
            style={{ marginTop: '20px' }}
          />
        </BaseView>
      </BaseView>
    )
  }

  if (addressError) {
    return (
      <BaseView>
        <StickyHeaderNavigation title={t('translation:deposit')} />
        <BaseView flex={1} bgColor="background.screen" center gap={20}>
          <BaseText color="label.default" textAlign="center">
            Failed to load wallet address
          </BaseText>
          <AppButton label="Go Back" onClick={() => navigate(-1)} />
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView
      style={{
        paddingTop: `${offsets.top + 46}px`,
        height: '100vh',
        paddingBottom: `${offsets.bottom}px`,
        // paddingLeft: '13px',
        // paddingRight: '13px',
      }}
    >
      {/* Fixed Header Navigation */}
      <StickyHeaderNavigation
        title={`${t('translation:deposit')} ${currency.code}`}
      />

      {/* Main Content Container */}
      <BaseView
        flex={1}
        px={12}
        bgColor="background.screen"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflowY: 'auto',
        }}
      >
        {/* Top Content - QR Code and Buttons */}
        <BaseView
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 28,
            paddingTop: 24,
          }}
        >
          {/* QR Code */}
          <BaseView center>
            <QRCodeCard
              value={walletAddress}
              size={230}
              className={isLoadingAddress ? 'animate-pulse bg-gray-600' : ''}
            />
          </BaseView>

          {/* Minimum Deposit Info */}
          <BaseView center gap={12}>
            <BaseView row align="center" gap={4} center>
              <BaseText
                fontSize={16}
                fontFamily="semibold"
                style={{ color: theme.colors.background.buttonPrimaryDisabled }}
              >
                {t('translation:minimumDeposit', 'Minimum deposit')}{' '}
                {formatDynamicCurrency({
                  amount: minimumDepositAmount || 0,
                  currencyCode: currency.code,
                  showDecimals: true,
                })}
              </BaseText>
              <button
                onClick={() => setIsInstructionsModalOpen(true)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Deposit instructions"
              >
                <SmallInfo
                  size={22}
                  color={theme.colors.background.buttonPrimaryDisabled}
                  secondColor={theme.colors.background.screen}
                />
              </button>
            </BaseView>

            {/* Formatted Address */}
            <BaseText
              lineHeight={40}
              fontSize={34}
              fontFamily="semibold"
              color="label.default"
              textAlign="center"
              className={
                isLoadingAddress ? 'animate-pulse bg-gray-600 rounded' : ''
              }
              style={{
                minHeight: isLoadingAddress ? '24px' : 'auto',
                minWidth: isLoadingAddress ? '300px' : 'auto',
              }}
            >
              {!isLoadingAddress && walletAddress
                ? formatAddress(walletAddress)
                : ''}
            </BaseText>
          </BaseView>

          {/* Action Buttons */}
          <BaseView
            row
            gap={8}
            style={{ marginLeft: 'auto', marginRight: 'auto' }}
            pb={32}
            center
          >
            <AppButton
              label={t('translation:copyAddress', 'Copy address')}
              iconName="copy"
              iconPosition="right"
              iconGap={14}
              iconSize={20}
              onClick={handleCopyAddress}
              disabled={!walletAddress || isLoadingAddress}
              isSharedSpace
              style={{
                borderRadius: '20px',
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '16px',
                paddingRight: '16px',
                height: '54px',
                width: '100%',
              }}
            />

            <AppButton
              type="secondaryGray"
              iconName="upload"
              iconSize={24}
              onClick={handleShareAddress}
              disabled={!walletAddress || isLoadingAddress}
              style={{
                borderRadius: '20px',
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '12px',
                paddingRight: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                width: '54px',
                height: '54px',
              }}
            />
          </BaseView>
        </BaseView>

        {/* Fixed Footer Content */}
        <BaseView
          gap={20}
          style={{
            // paddingBottom: '20px',
            marginTop: 'auto',
          }}
        >
          {/* Network Selector */}
          {selectedBlockchain && (
            <NetworkSelector
              blockchain={selectedBlockchain}
              onPress={handleOpenBlockchainModal}
              isLoading={isLoadingBlockchains}
            />
          )}

          {/* Warning */}
          <BaseView row align="flex-start" gap={8}>
            <SmallInfo
              size={20}
              color={theme.colors.background.buttonPrimaryDisabled}
              secondColor={theme.colors.background.screen}
            />
            <BaseView flex={1}>
              <BaseText
                fontSize={14}
                color="label.description"
                style={{ lineHeight: '20px' }}
              >
                {t(
                  'abbreviationToCorrectNetwork',
                  'Ensure you have the correct network, deposits from other networks or assets will be lost.',
                )}
              </BaseText>
            </BaseView>
          </BaseView>
        </BaseView>
      </BaseView>

      {/* Blockchain Selection Modal */}
      <SelectBlockchainModal
        isOpen={isBlockchainModalOpen}
        onClose={() => setIsBlockchainModalOpen(false)}
        onSelect={handleBlockchainSelect}
        blockchains={blockchains}
        selectedBlockchain={selectedBlockchain || undefined}
        currencyCode={currency?.code || ''}
      />

      {/* Deposit Instructions Modal */}
      <DepositInstructionsModal
        isOpen={isInstructionsModalOpen}
        onClose={() => setIsInstructionsModalOpen(false)}
        minimumDepositAmount={minimumDepositAmount}
        currencyCode={currency.code}
        networkArrivalTime={selectedBlockchain?.arrival_time}
        isLoading={isLoadingCurrencyInfo}
      />
    </BaseView>
  )
}
